/**
 * Tenant administration service — admin-only.
 *
 * Wraps /api/v1/admin/tenants/* for the multi-tenancy admin UI.
 *
 * Auth note: uses the shared axios instance from `../shared/services/api`, which
 * attaches the Bearer token automatically via its request interceptor (reads from
 * the Zustand authStore). No manual token handling needed here.
 */
import api from '../shared/services/api';

export type TenantStatus = 'active' | 'suspended';

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan_id: string;
  plan_code: string | null;
  status: TenantStatus;
  monthly_document_limit: number;
  monthly_documents_used: number;
}

export interface TenantCreate {
  name: string;
  slug: string;
  plan_code: string;
}

export interface Plan {
  id: string;
  code: string;
  name: string;
  monthly_document_limit: number;
  default_module_codes: string[];
  max_module_codes: string[];
  is_active: boolean;
}

/** GET /api/v1/admin/tenants/plans — active plans available to assign to tenants. */
export async function listPlans(): Promise<Plan[]> {
  const r = await api.get<Plan[]>('/api/v1/admin/tenants/plans');
  return r.data;
}

/** GET /api/v1/admin/tenants */
export async function listTenants(): Promise<Tenant[]> {
  const r = await api.get<Tenant[]>('/api/v1/admin/tenants');
  return r.data;
}

/** POST /api/v1/admin/tenants — returns the created tenant (201). */
export async function createTenant(payload: TenantCreate): Promise<Tenant> {
  const r = await api.post<Tenant>('/api/v1/admin/tenants', payload);
  return r.data;
}

/** PUT /api/v1/admin/tenants/{id}/plan — change the tenant's subscription plan. */
export async function updateTenantPlan(id: string, planCode: string): Promise<Tenant> {
  const r = await api.put<Tenant>(`/api/v1/admin/tenants/${id}/plan`, {
    plan_code: planCode,
  });
  return r.data;
}

export interface TenantModuleState {
  module_id: string;
  name: string;
  code: string | null;
  enabled: boolean;
}

/**
 * GET /api/v1/admin/tenants/{id}/modules — list all globally-enabled modules
 * together with this tenant's enablement state.
 */
export async function listTenantModules(tenantId: string): Promise<TenantModuleState[]> {
  const r = await api.get<TenantModuleState[]>(`/api/v1/admin/tenants/${tenantId}/modules`);
  return r.data;
}

/** PUT /api/v1/admin/tenants/{id}/modules — enable/disable a module for the tenant. */
export async function toggleTenantModule(
  id: string,
  moduleId: string,
  enabled: boolean,
): Promise<{ status: string }> {
  const r = await api.put<{ status: string }>(`/api/v1/admin/tenants/${id}/modules`, {
    module_id: moduleId,
    enabled,
  });
  return r.data;
}

/**
 * PUT /api/v1/admin/tenants/{id}/status?status_value=active|suspended
 *
 * The backend takes the new status as a query param (not a body), so it is
 * passed via axios `params`.
 */
export async function setTenantStatus(id: string, status: TenantStatus): Promise<Tenant> {
  const r = await api.put<Tenant>(`/api/v1/admin/tenants/${id}/status`, null, {
    params: { status_value: status },
  });
  return r.data;
}

// ---------------------------------------------------------------------------
// Tenant-admin user management — /api/v1/tenant/users
//
// Scoped to the calling user's own tenant by the backend (tenant_id is derived
// from the JWT, not passed by the client). Used by the Tenant-Admin Users page.
// ---------------------------------------------------------------------------

export type TenantUserRole = 'user' | 'tenant_admin';

export interface TenantUser {
  id: string;
  email: string;
  username: string;
  firstname: string;
  lastname: string;
  phonenumber?: string | null;
  role: string;
  is_active: boolean;
  created_at: string;
}

export interface TenantUserCreate {
  email: string;
  username: string;
  firstname: string;
  lastname: string;
  password: string;
  role: TenantUserRole;
}

/** GET /api/v1/tenant/users — list users belonging to the caller's tenant. */
export async function listTenantUsers(): Promise<TenantUser[]> {
  const r = await api.get<TenantUser[]>('/api/v1/tenant/users');
  return r.data;
}

/** POST /api/v1/tenant/users — create a user within the caller's tenant. */
export async function createTenantUser(payload: TenantUserCreate): Promise<TenantUser> {
  const r = await api.post<TenantUser>('/api/v1/tenant/users', payload);
  return r.data;
}

/** PUT /api/v1/tenant/users/{id}/active — activate or deactivate a tenant user. */
export async function setTenantUserActive(id: string, isActive: boolean): Promise<TenantUser> {
  const r = await api.put<TenantUser>(`/api/v1/tenant/users/${id}/active`, {
    is_active: isActive,
  });
  return r.data;
}

export interface TenantUserUpdate {
  firstname?: string;
  lastname?: string;
  email?: string;
  phonenumber?: string;
  role?: TenantUserRole;
}

/** PUT /api/v1/tenant/users/{id} — edit a user's profile and/or role within the
 *  caller's own tenant. Role is restricted to user | tenant_admin server-side. */
export async function updateTenantUser(id: string, body: TenantUserUpdate): Promise<TenantUser> {
  const r = await api.put<TenantUser>(`/api/v1/tenant/users/${id}`, body);
  return r.data;
}

/** POST /api/v1/tenant/users/{id}/reset-password — set a new password for a
 *  user in the caller's own tenant (min 8 chars, enforced server-side). */
export async function resetTenantUserPassword(
  id: string,
  newPassword: string,
): Promise<{ message: string; user_email: string }> {
  const r = await api.post(`/api/v1/tenant/users/${id}/reset-password`, {
    new_password: newPassword,
  });
  return r.data;
}

/** DELETE /api/v1/tenant/users/{id} — delete a user in the caller's own tenant
 *  (users with transaction history are deactivated instead of hard-deleted). */
export async function deleteTenantUser(id: string): Promise<{ message: string; action: string }> {
  const r = await api.delete(`/api/v1/tenant/users/${id}`);
  return r.data;
}

/** PUT /api/v1/tenant/organisation — edit the caller's own organisation profile
 *  (display name only; plan/modules/quota stay super-admin-controlled). */
export async function updateTenantOrganisation(name: string): Promise<TenantInfo> {
  const r = await api.put<TenantInfo>('/api/v1/tenant/organisation', { name });
  return r.data;
}

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  plan_code: string | null;
  plan_name: string | null;
  monthly_document_limit: number;
  monthly_documents_used: number;
}

/** GET /api/v1/tenant/info — the caller's own tenant: plan + pooled quota usage. */
export async function getTenantInfo(): Promise<TenantInfo> {
  const r = await api.get<TenantInfo>('/api/v1/tenant/info');
  return r.data;
}

// ---------------------------------------------------------------------------
// Super-admin per-tenant user management — /api/v1/admin/tenants/{id}/users
//
// Unlike the tenant-admin endpoints above, these take an explicit tenant_id so a
// super admin can manage users in ANY tenant (e.g. bootstrap a new tenant's first
// tenant_admin). Used by the SuperAdminTenants page.
// ---------------------------------------------------------------------------

/** GET /api/v1/admin/tenants/{id}/users — list users in the given tenant. */
export async function adminListTenantUsers(tenantId: string): Promise<TenantUser[]> {
  const r = await api.get<TenantUser[]>(`/api/v1/admin/tenants/${tenantId}/users`);
  return r.data;
}

/** POST /api/v1/admin/tenants/{id}/users — create a user in the given tenant (201). */
export async function adminCreateTenantUser(
  tenantId: string,
  body: TenantUserCreate,
): Promise<TenantUser> {
  const r = await api.post<TenantUser>(`/api/v1/admin/tenants/${tenantId}/users`, body);
  return r.data;
}

/** PUT /api/v1/admin/tenants/{id}/users/{userId}/active — activate/deactivate a user. */
export async function adminSetTenantUserActive(
  tenantId: string,
  userId: string,
  isActive: boolean,
): Promise<TenantUser> {
  const r = await api.put<TenantUser>(
    `/api/v1/admin/tenants/${tenantId}/users/${userId}/active`,
    { is_active: isActive },
  );
  return r.data;
}

/** PUT /api/v1/admin/tenants/{id}/users/{userId} — change a user's tenant role
 *  (user | tenant_admin). super_admin is rejected server-side. */
export async function adminUpdateTenantUser(
  tenantId: string,
  userId: string,
  body: { role?: TenantUserRole },
): Promise<TenantUser> {
  const r = await api.put<TenantUser>(
    `/api/v1/admin/tenants/${tenantId}/users/${userId}`,
    body,
  );
  return r.data;
}
